import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Box, Typography, Tabs, Tab } from '@ellucian/react-design-system/core';
import { Icon } from '@ellucian/ds-icons/lib';
import { sanitizeContent, getYouTubeEmbedUrl, decodeAndCleanText } from '../../utils/helpers';

function a11yProps(index: number, isExpanded: boolean) {
  return {
    id: `solution-tab-${index}`,
    'aria-controls': `solution-tabpanel-${index}`,
    'aria-expanded': isExpanded,
    tabIndex: 0,  
  };
}

interface Review {
  id: number;
  title?: string;
  text: string;
  institution?: string;
}

interface SolutionTabsProps {
  activeTab: number;
  setActiveTab: (tab: number) => void;
  description: string;
  image: string;
  headline?: string;
  solutionVideo?: string | null;
  reviews: Review[];
  features: string[];
  useCases?: string[];
  dependencies: string[];
  supportedRegions: string[];
  supportedLanguages: string[];
  integratedEllucianSolutions?: string[];
  requirements?: { icon?: string; name: string; version: string }[];
  isMobile?: boolean;
  isTablet?: boolean;
}

const SolutionTabs: React.FC<SolutionTabsProps> = ({
  activeTab,
  setActiveTab,
  description,
  image,
  solutionVideo,
  reviews,
  features,
  useCases,
  dependencies,
  supportedRegions,
  supportedLanguages,
  integratedEllucianSolutions,
  requirements,
  isMobile = false,
  isTablet = false
}) => {
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // --- Tab underline dynamic positioning ---
  const tabRef0 = useRef<HTMLElement>(null);
  const tabRef1 = useRef<HTMLElement>(null);
  const tabRefs = useMemo(() => [tabRef0, tabRef1], []);
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });

  // Track if tab change was triggered by keyboard interaction
  const isKeyboardNavigation = useRef(false);

  useEffect(() => {
    const node = tabRefs[activeTab].current as HTMLElement | null;
    if (node && node.parentNode) {
      const rect = node.getBoundingClientRect();
      const parentRect = (node.parentNode as HTMLElement).getBoundingClientRect();
      setIndicatorStyle({ left: rect.left - parentRect.left, width: rect.width });
    }
  }, [activeTab, tabRefs]);

  // Focus management: move focus to the active tab panel
  useEffect(() => {
    if (isKeyboardNavigation.current) {
      const panelId = `solution-tabpanel-${activeTab}`;
      // Small delay to ensure the panel is rendered
      setTimeout(() => {
        const panel = document.getElementById(panelId);
        if (panel) {
          panel.focus();
        }
      }, 0);
      isKeyboardNavigation.current = false;
    }
  }, [activeTab]);

  const handleTabKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      isKeyboardNavigation.current = true;
    }
  };

    return (
    <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        width: '100%', 
        gap: isMobile ? '24px' : (isTablet ? '28px' : '33px'), 
        ml: 0, 
        pl: 0, 
        background: '#fff',
        padding: isMobile ? '0 0' : (isTablet ? '0 16px' : '0')
    }}>
  {/* Tabs + Line */}
  <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', ml: 0, pl: 0, position: 'sticky', top: 0, zIndex: 10, background: '#fff' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          onKeyDown={handleTabKeyDown}
          aria-label="solution tabs"
          TabIndicatorProps={{ style: { display: 'none' } }}
          sx={{
            pl: 0,
            ml: 0,
            minHeight: 48,
            mb: 0,
            '& .MuiTabs-flexContainer': { pl: 0, ml: 0, gap: '20px' },
            '& .MuiTab-root': {
              pl: 0,
              ml: 0,
              textTransform: 'none',
              color: 'var(--Neutral-neutral-600, #151618)',
              fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto',
              fontSize: '16px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: '24px',
              minHeight: 48,
            },
            '& .Mui-selected': {
              color: 'var(--Interactive-Blue-cta-blue-600, #026BC8)',
              fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto',
              fontSize: '16px',
              fontStyle: 'normal',
              fontWeight: 700,
              lineHeight: '24px',
            },
            '& .MuiTab-root:focus-visible': {
              outline: 'none',
              boxShadow: '0 0 0 3px rgba(3,169,244,0.18)',
            },
          }}
        >
          <Tab label="Overview" sx={{ pl: 0, ml: 0 }} ref={tabRefs[0]} {...a11yProps(0, activeTab === 0)} />
          <Tab label="Technical Details" sx={{ pl: 0, ml: 0 }} ref={tabRefs[1]} {...a11yProps(1, activeTab === 1)} />
        </Tabs>
        <Box
          sx={{
            width: '100%',
            height: '2px',
            position: 'relative',
            ml: 0,
            pl: 0,
            background: 'var(--Neutral-neutral-300, #D9D9D9)',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              left: indicatorStyle.left,
              width: indicatorStyle.width,
              height: '2px',
              background: '#0061A8',
              top: 0,
              zIndex: 2,
              transition: 'left 0.3s, width 0.3s',
            }}
          />
        </Box>
      </Box>

      {/* ---------------- Overview Tab ---------------- */}
      {activeTab === 0 && (
        <Box
          role="tabpanel"
          id="solution-tabpanel-0"
          aria-labelledby="solution-tab-0"
          sx={{ mt: 0, ml: 0, pl: 0, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'flex-start', gap: { xs: 0, md: 6 } }}
        >
          {/* Video, description, and features with adaptive width */}
          <Box sx={{ 
            flex: 1, 
            maxWidth: { 
              xs: '100%',
              sm: '100%',
              md: '100%',
              lg: '100%',
              xl: '100%'
            }, 
            width: '100%',
            pr: { md: 4 }, 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '32px'
          }}>
            {/* Video section - only show if solutionVideo exists and is valid */}
            {(() => {
              const embedUrl = getYouTubeEmbedUrl(solutionVideo);
              if (!embedUrl) return null;
              
              return (
                <Box
                  sx={{
                    borderRadius: 2,
                    overflow: 'hidden',
                    mb: 0,
                    height: isMobile ? '240px' : (isTablet ? '350px' : '450px'),
                    width: '100%',
                    maxWidth: '100%',
                    aspectRatio: isMobile ? '16/9' : '16/9',
                    position: 'relative',
                    background: 'url(<path-to-image>) lightgray 50% / cover no-repeat',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <iframe
                    width="100%"
                    height="100%"
                    src={embedUrl}
                    title="YouTube video player"
                    frameBorder={0}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    style={{ position: 'absolute', inset: 0, border: 0 }}
                  />
                </Box>
              );
            })()}
            {/* Description section */}
            <Box>
              <Typography
                variant="h2"
                sx={{
                  mb: '16px',
                  color: '#151618',
                  fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto',
                  fontSize: isMobile ? '20px' : (isTablet ? '22px' : '24px'),
                  fontStyle: 'normal',
                  fontWeight: 700,
                  lineHeight: isMobile ? '28px' : (isTablet ? '32px' : '36px'),
                  letterSpacing: isMobile ? '0.6px' : (isTablet ? '0.66px' : '0.72px'),
                }}
              >
                Description
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: '#151618',
                  fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto',
                  fontSize: '16px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: '24px',
                  mt: 2,
                }}
                dangerouslySetInnerHTML={sanitizeContent(description)}
              >
              </Typography>
            </Box>
            {/* Features block */}
            {features && features.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%', maxWidth: '100%' }}>
                <Typography
                  variant="h2"
                  sx={{
                    mb: '16px',
                    color: '#151618',
                    fontFamily: '"Proxima Nova", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Helvetica, Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                    fontSize: isMobile ? '20px' : (isTablet ? '22px' : '24px'),
                    fontStyle: 'normal',
                    fontWeight: 700,
                    lineHeight: isMobile ? '28px' : (isTablet ? '32px' : '36px'),
                    letterSpacing: isMobile ? '0.6px' : (isTablet ? '0.66px' : '0.72px'),
                  }}
                >
                  Features
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', alignItems: 'flex-start', width: '100%' }}>
                  {features.map((feature, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '16px',
                      }}
                    >
                      <Box sx={{ flexShrink: 0, width: 16, height: 16, mt: '4px' }}>
                        <Icon name="check" style={{ width: 16, height: 16, color: '#151618', display: 'block' }} />
                      </Box>
                      <Typography
                        variant="body1"
                        sx={{
                          color: '#151618',
                          fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto',
                          fontSize: '16px',
                          fontStyle: 'normal',
                          fontWeight: 400,
                          lineHeight: '24px',
                          m: 0,
                          p: 0,
                        }}
                      >
                        {decodeAndCleanText(feature)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Use Cases block */}
            {useCases && useCases.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%', maxWidth: '100%' }}>
                <Typography
                  variant="h2"
                  sx={{
                    mb: '16px',
                    color: '#151618',
                    fontFamily: '"Proxima Nova", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Helvetica, Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                    fontSize: isMobile ? '20px' : (isTablet ? '22px' : '24px'),
                    fontStyle: 'normal',
                    fontWeight: 700,
                    lineHeight: isMobile ? '28px' : (isTablet ? '32px' : '36px'),
                    letterSpacing: isMobile ? '0.6px' : (isTablet ? '0.66px' : '0.72px'),
                  }}
                >
                  Use Cases
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    paddingLeft: '24px',
                    margin: 0,
                    width: '100%',
                    listStyleType: 'disc',
                  }}
                >
                  {useCases.map((useCase, idx) => (
                    <Box
                      key={idx}
                      component="li"
                      sx={{
                        marginBottom: idx === useCases.length - 1 ? 0 : '16px',
                        color: '#151618',
                        fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto',
                        fontSize: '16px',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        lineHeight: '24px',
                      }}
                    >
                      {decodeAndCleanText(useCase)}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* 32px space after features/use cases list */}
            <Box sx={{ height: '32px' }} />
          </Box>
        </Box>
      )}

      {/* ---------------- Technical Details Tab ---------------- */}
      {activeTab === 1 && (
        <Box
          role="tabpanel"
          id="solution-tabpanel-1"
          aria-labelledby="solution-tab-1"
          sx={{
            mt: 0,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            gap: 0,
            px: 0,
            boxSizing: 'border-box',
          }}
        >
          {/* Main technical details content */}
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: { xs: '32px', md: '48px' },
            }}
          >
            {/* Integrated Ellucian Solutions */}
            <Box>
              <Typography
                variant="h2"
                sx={{
                  mb: { xs: '8px', md: '16px' },
                  color: '#151618',
                  fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto',
                  fontSize: { xs: '18px', sm: '20px', md: '22px', lg: '24px' },
                  fontWeight: 700,
                  lineHeight: { xs: '26px', sm: '28px', md: '32px', lg: '36px' },
                  letterSpacing: { xs: '0.5px', sm: '0.6px', md: '0.66px', lg: '0.72px' },
                }}
              >
                Integrated Ellucian Solutions
              </Typography>
              {integratedEllucianSolutions && integratedEllucianSolutions.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  {integratedEllucianSolutions.map((solution, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'row', sm: 'row' },
                        alignItems: { xs: 'center', sm: 'center' },
                        alignSelf: 'stretch',
                        minHeight: { xs: 'auto', sm: '40px' },
                        background: 'transparent',
                        borderRadius: 0,
                        p: 0,
                        width: 'auto',
                        maxWidth: '100%',
                        mb: idx === integratedEllucianSolutions.length - 1 ? 0 : { xs: '12px', md: '8px' },
                        gap: { xs: '12px', sm: '16px' },
                      }}
                    >
                      <Icon name="settings" style={{ width: 16, height: 16, color: '#5B5E65' }} />
                      <Typography
                        sx={{
                          color: '#151618',
                          fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto',
                          fontSize: { xs: '15px', sm: '16px' },
                          fontWeight: 400,
                          lineHeight: { xs: '22px', sm: '24px' },
                          mb: 0,
                        }}
                      >
                        {solution}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}

              {/* Dependencies Section */}
            </Box>

            {dependencies && dependencies.length > 0 && (
            <Box>
              <Typography
                variant="h2"
                sx={{
                  mb: { xs: '8px', md: '16px' },
                  color: '#151618',
                  fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto',
                  fontSize: { xs: '18px', sm: '20px', md: '22px', lg: '24px' },
                  fontWeight: 700,
                  lineHeight: { xs: '26px', sm: '28px', md: '32px', lg: '36px' },
                  letterSpacing: { xs: '0.5px', sm: '0.6px', md: '0.66px', lg: '0.72px' },
                }}
              >
                Dependencies
              </Typography>
              <ul style={{ paddingLeft: 24, marginBottom: 16, marginTop: 0 }}>
                {dependencies.map((dep, idx, arr) => (
                  <li
                    key={idx}
                    style={{
                      marginBottom: idx === arr.length - 1 ? 0 : 8,
                      fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto',
                      fontSize: 16,
                      color: '#151618',
                    }}
                  >
                    {dep}
                  </li>
                ))}
              </ul>
            </Box>
            )}

            {/* Region & Country */}
            {supportedRegions && supportedRegions.length > 0 && (
            <Box>
              <Typography
                variant="h2"
                sx={{
                  mb: { xs: '8px', md: '16px' },
                  color: '#151618',
                  fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto',
                  fontSize: { xs: '18px', sm: '20px', md: '22px', lg: '24px' },
                  fontWeight: 700,
                  lineHeight: { xs: '26px', sm: '28px', md: '32px', lg: '36px' },
                  letterSpacing: { xs: '0.5px', sm: '0.6px', md: '0.66px', lg: '0.72px' },
                }}
              >
                Region & Country
              </Typography>
              <ul style={{ paddingLeft: 24, marginBottom: 16, marginTop: 0 }}>
                {supportedRegions.map((region, idx, arr) => (
                  <li
                    key={idx}
                    style={{
                      marginBottom: idx === arr.length - 1 ? 0 : 8,
                      fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto',
                      fontSize: 16,
                      color: '#151618',
                    }}
                  >
                    {region}
                  </li>
                ))}
              </ul>
            </Box>
            )}

            {/* Supported Languages */}
            {supportedLanguages && supportedLanguages.length > 0 && (
            <Box>
              <Typography
                variant="h2"
                sx={{
                  mb: { xs: '8px', md: '16px' },
                  color: '#151618',
                  fontFamily: '"Proxima Nova", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Helvetica, Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                  fontSize: { xs: '18px', sm: '20px', md: '22px', lg: '24px' },
                  fontWeight: 700,
                  lineHeight: { xs: '26px', sm: '28px', md: '32px', lg: '36px' },
                  letterSpacing: { xs: '0.5px', sm: '0.6px', md: '0.66px', lg: '0.72px' },
                }}
              >
                Supported Languages
              </Typography>
              <ul style={{ paddingLeft: 24, marginBottom: 16, marginTop: 0 }}>
                {supportedLanguages.map((language, idx, arr) => (
                  <li
                    key={idx}
                    style={{
                      marginBottom: idx === arr.length - 1 ? 0 : 8,
                      fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto',
                      fontSize: 16,
                      color: '#151618',
                    }}
                  >
                    {language}
                  </li>
                ))}
              </ul>
            </Box>
            )}
            {/* Add spacing below Supported Languages */}
            <Box sx={{ height: { xs: '24px', md: '32px' } }} />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default SolutionTabs;